import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import FeaturedProducts from '../components/FeaturedProducts';
import HomeFaqSection from '../components/HomeFaqSection';
import Footer from '../components/Footer';
import { ALL_GAME_NAMES, getGameSlug } from '../config/allGames';
import { getGameIcon } from '../utils/getGameIcon';
import '../styles/HomePage.css';
import '../styles/DsHome.css';

function tile(name, category = 'accounts', extra = {}) {
  return {
    name,
    to: `/${getGameSlug(name)}/${category}`,
    icon: extra.icon || getGameIcon(name),
    badge: extra.badge,
    kicker: extra.kicker || (category === 'accounts' ? 'Accounts' : category === 'top-ups' ? 'Top-ups' : category === 'boosting' ? 'Boosting' : 'Items'),
  };
}

const HERO_SLIDES = [
  { src: '/ds/hero-accounts.jpg', alt: 'Game accounts marketplace' },
  { src: '/ds/hero-boosting.jpg', alt: 'Rank boosting' },
  { src: '/ds/hero-topups.jpg', alt: 'Game top-ups' },
];

const PAY_LOGOS = [
  { src: '/main/payments/visa.svg', alt: 'Visa' },
  { src: '/main/payments/mastercard.svg', alt: 'Mastercard' },
  { src: '/main/payments/amex.svg', alt: 'American Express' },
  { src: '/main/payments/paypal.svg', alt: 'PayPal' },
  { src: '/main/payments/btc.svg', alt: 'Bitcoin' },
  { src: '/main/payments/eth.svg', alt: 'Ethereum' },
  { src: '/main/payments/litecoin.svg', alt: 'Litecoin' },
  { src: '/main/payments/applepaywhitetheme.svg', alt: 'Apple Pay' },
  { src: '/main/payments/googleplaywhitetheme.svg', alt: 'Google Pay' },
  { src: '/main/payments/klarna.svg', alt: 'Klarna' },
];

const POPULAR = [
  tile('Roblox'),
  tile('Fortnite'),
  tile('Valorant'),
  tile('Minecraft'),
  tile('Counter-Strike 2'),
  tile('Brawl Stars', 'top-ups'),
  tile('Discord'),
  tile('PUBG Mobile'),
  tile('League of Legends'),
  tile('Genshin Impact', 'top-ups'),
  tile('Mobile Legends'),
  tile('Marvel Rivals', 'boosting', { kicker: 'Boosting' }),
];

const GAMES = [
  tile('Roblox'),
  tile('Minecraft'),
  tile('Fortnite'),
  tile('Valorant'),
  tile('Genshin Impact', 'top-ups'),
  tile('Counter-Strike 2'),
  tile('Dota 2'),
  tile('Rust'),
];

const APPS = [
  tile('Discord'),
  tile('Spotify'),
  tile('Netflix'),
  tile('Crunchyroll'),
  tile('Xbox'),
  tile('PlayStation'),
  tile('Epic Games'),
  tile('Battlenet', 'accounts', { icon: '/main/games/icons/battlenet.jpg' }),
];

function GamesCarousel({ items }) {
  const trackRef = useRef(null);

  const scrollByPage = (direction) => {
    const track = trackRef.current;
    if (!track) return;
    const amount = Math.max(track.clientWidth * 0.72, 280);
    track.scrollBy({ left: direction * amount, behavior: 'smooth' });
  };

  return (
    <div className="ds-carousel">
      <button type="button" className="ds-carousel-btn" aria-label="Previous games" onClick={() => scrollByPage(-1)}>
        <img src="/ds/chevron-left.svg" alt="" width={16} height={16} />
      </button>
      <div className="ds-carousel-track" ref={trackRef}>
        {items.map((item) => (
          <Link key={item.name} to={item.to} className="ds-top">
            <span className="ds-top-media">
              {item.icon ? <img src={item.icon} alt="" width={88} height={88} /> : <span>{item.name[0]}</span>}
            </span>
            <em>{item.name}</em>
            <strong>{item.kicker}</strong>
          </Link>
        ))}
      </div>
      <button type="button" className="ds-carousel-btn" aria-label="Next games" onClick={() => scrollByPage(1)}>
        <img src="/ds/chevron-right.svg" alt="" width={16} height={16} />
      </button>
    </div>
  );
}

function SlotTile({ item }) {
  return (
    <Link to={item.to} className="ds-tile">
      {item.icon ? <img src={item.icon} alt="" /> : <span className="ds-tile-fallback">{item.name[0]}</span>}
      <span className="ds-tile-copy">
        <strong>{item.name}</strong>
        <em>{item.kicker}</em>
      </span>
    </Link>
  );
}

function SectionHead({ id, title, note, to }) {
  return (
    <div className="ds-sec-head">
      <div className="ds-sec-copy">
        <h2 id={id}>{title}</h2>
        {note ? <p>{note}</p> : null}
      </div>
      {to ? <Link to={to} className="ds-viewall">View all</Link> : null}
    </div>
  );
}

function HomeHeroBanner() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => {
      setActive((index) => (index + 1) % HERO_SLIDES.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, []);

  const prev = () => setActive((index) => (index + HERO_SLIDES.length - 1) % HERO_SLIDES.length);
  const next = () => setActive((index) => (index + 1) % HERO_SLIDES.length);
  const slide = HERO_SLIDES[active];

  return (
    <div className="ds-hero-wrap">
      <div className="ds-hero">
        <div className="ds-hero-slide">
          <div className="ds-hero-slide-inner">
            <img src={slide.src} alt={slide.alt} />
            <div className="ds-hero-arrows">
              <button type="button" aria-label="Previous" onClick={prev}>
                <img src="/ds/chevron-left.svg" alt="" width={16} height={16} />
              </button>
              <button type="button" aria-label="Next" onClick={next}>
                <img src="/ds/chevron-right.svg" alt="" width={16} height={16} />
              </button>
            </div>
          </div>
          <div className="ds-hero-progress">
            {HERO_SLIDES.map((item, index) => (
              <span key={item.src} className={index === active ? 'is-on' : ''} />
            ))}
          </div>
        </div>
        <Link to="/fortnite/accounts" className="ds-hero-promo">
          <div className="ds-hero-promo-inner">
            <img src="/ds/hero-escrow.jpg" alt="Escrow protected checkout" />
          </div>
        </Link>
      </div>
    </div>
  );
}

function HomePage() {
  const catalog = useMemo(
    () => ALL_GAME_NAMES.map((name) => tile(name)),
    []
  );

  return (
    <>
      <div className="ds-pay" aria-hidden>
        <div className="ds-pay-track">
          {[...PAY_LOGOS, ...PAY_LOGOS].map((logo, index) => (
            <img key={`${logo.alt}-${index}`} src={logo.src} alt="" height={14} />
          ))}
        </div>
      </div>

      <HomeHeroBanner />

      <section className="ds-sec" aria-labelledby="ds-top-heading">
        <SectionHead id="ds-top-heading" title="All games" />
        <GamesCarousel items={catalog} />
      </section>

      <section className="ds-sec" aria-labelledby="ds-popular-heading">
        <SectionHead id="ds-popular-heading" title="Popular" note="Most traded right now" to="/fortnite/accounts" />
        <div className="ds-tile-grid">
          {POPULAR.map((item) => (
            <SlotTile key={item.name} item={item} />
          ))}
        </div>
      </section>

      <section className="ds-sec" aria-labelledby="ds-games-heading">
        <SectionHead id="ds-games-heading" title="Games" note="Fresh this week" to="/fortnite/accounts" />
        <div className="ds-tile-grid">
          {GAMES.map((item) => (
            <SlotTile key={item.name} item={item} />
          ))}
        </div>
      </section>

      <section className="ds-sec" aria-labelledby="ds-apps-heading">
        <SectionHead id="ds-apps-heading" title="Apps" to="/netflix/accounts" />
        <div className="ds-tile-grid">
          {APPS.map((item) => (
            <SlotTile key={item.name} item={item} />
          ))}
        </div>
      </section>

      <FeaturedProducts maxAds={12} variant="home" />
      <HomeFaqSection />
      <Footer />
    </>
  );
}

export default HomePage;
